// src/pages/Acoes/components/PlanoAcaoCard.jsx
import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { formatarDataHora } from '../../../utils/coleta';
import ResultadoAmostra from './ResultadoAmostra';

const dataHora = (iso) => (iso ? formatarDataHora(new Date(iso)) : '');

function Situacao({ plano, vencida }) {
    if (plano.status === 'CONCLUIDA') {
        return <Chip size="small" color="success" variant="outlined" icon={<Lucide.CircleCheck size={14} />} label="Concluída" sx={{ fontWeight: 600 }} />;
    }
    if (vencida) {
        return <Chip size="small" color="error" variant="outlined" icon={<Lucide.Clock size={14} />} label="Vencida" sx={{ fontWeight: 600 }} />;
    }
    return <Chip size="small" color="warning" variant="outlined" icon={<Lucide.Clock size={14} />} label="Em aberto" sx={{ fontWeight: 600 }} />;
}

/**
 * Um plano de ação: o que fazer, quem e até quando; o resultado antes e depois da
 * intervenção (mesmo ponto, origem e turno); e, concluída, o registro do produtor.
 */
export default function PlanoAcaoCard({
    item,
    unidadeDe,
    podeGerenciar,
    podeExcluir,
    onConcluir,
    onEditar,
    onExcluir,
    onAbrirAmostra,
}) {
    const theme = useTheme();
    const { plano, antes, depois, depoisAutomatico, vencida } = item;
    const concluida = plano.status === 'CONCLUIDA';
    const [menu, setMenu] = useState(null);
    const corIcone = concluida ? theme.palette.success.main : theme.palette.warning.main;
    const temMenu = !concluida && (podeGerenciar || podeExcluir);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(corIcone, 0.12), color: corIcone, display: 'flex', flexShrink: 0 }}>
                        <Lucide.ClipboardList size={20} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={700}>{plano.titulo}</Typography>
                        {plano.motivo && (
                            <Typography variant="body2" color="text.secondary">{plano.motivo}</Typography>
                        )}
                        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 1 }}>
                            <Situacao plano={plano} vencida={vencida} />
                        </Box>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }}>
                        <Situacao plano={plano} vencida={vencida} />
                    </Box>
                    {temMenu && (
                        <IconButton size="small" aria-label="Mais ações" onClick={(e) => setMenu(e.currentTarget)}>
                            <Lucide.EllipsisVertical size={18} />
                        </IconButton>
                    )}
                </Stack>

                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 2, color: 'text.secondary' }}>
                    {plano.responsavel && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Lucide.User size={14} />
                            <Typography variant="body2">{plano.responsavel}</Typography>
                        </Stack>
                    )}
                    {plano.prazo && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: vencida ? 'error.main' : undefined }}>
                            <Lucide.CalendarDays size={14} />
                            <Typography variant="body2">Prazo: {dataHora(plano.prazo)}</Typography>
                        </Stack>
                    )}
                </Stack>
                {plano.instrucao && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, whiteSpace: 'pre-line' }}>
                        {plano.instrucao}
                    </Typography>
                )}
            </Box>

            {antes && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                Antes da intervenção
                            </Typography>
                            <ResultadoAmostra resultado={antes} unidade={unidadeDe(antes.pagina)} onAbrir={onAbrirAmostra} />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                Depois da intervenção
                            </Typography>
                            {depois ? (
                                <ResultadoAmostra resultado={depois} unidade={unidadeDe(depois.pagina)} onAbrir={onAbrirAmostra} />
                            ) : (
                                <Typography variant="body2" color="text.secondary">Aguardando nova amostra</Typography>
                            )}
                        </Grid>
                    </Grid>
                    {depois && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                            {depoisAutomatico
                                ? 'Mesma origem, ponto, ordenha, método e unidade. A variação não comprova causalidade.'
                                : 'Amostra de verificação escolhida na conclusão. A variação não comprova causalidade.'}
                        </Typography>
                    )}
                </Box>
            )}

            {concluida && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" display="block">• Registro do produtor</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{plano.registro}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Executada em {dataHora(plano.execucaoEm)}
                        {plano.concluidaPor ? ` · registrada por ${plano.concluidaPor}` : ''}
                    </Typography>
                </Box>
            )}

            {!concluida && podeGerenciar && (
                <>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<Lucide.CheckCheck size={16} />}
                            onClick={() => onConcluir(item)}
                            sx={{ borderRadius: 2, borderColor: 'divider', fontWeight: 600 }}
                        >
                            Registrar conclusão
                        </Button>
                    </Box>
                </>
            )}

            <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
                {podeGerenciar && (
                    <MenuItem onClick={() => { setMenu(null); onEditar(item); }}>
                        <Lucide.Pencil size={16} style={{ marginRight: 8 }} /> Editar
                    </MenuItem>
                )}
                {podeExcluir && (
                    <MenuItem onClick={() => { setMenu(null); onExcluir(item); }} sx={{ color: 'error.main' }}>
                        <Lucide.Trash2 size={16} style={{ marginRight: 8 }} /> Excluir
                    </MenuItem>
                )}
            </Menu>
        </Paper>
    );
}
